import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadScript, GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { apiFetch } from './utils/api';
import PageHeader from './components/PageHeader';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

// Vehicle icon library
const VEHICLE_ICONS = {
  van: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path fill="COLOR" stroke="#fff" stroke-width="2" d="M8 18h24v8H8zm24 0l6 4v4h-6zm-24 8h-2a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h2zm24 0h2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-2zm-18 3a2 2 0 1 1-2 2 2 2 0 0 1 2-2zm12 0a2 2 0 1 1-2 2 2 2 0 0 1 2-2z"/></svg>`,
  truck: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path fill="COLOR" stroke="#fff" stroke-width="2" d="M6 16h20v10H6zm20 0l8 6v4h-8zm-20 10h-2v6h2zm20 0h2v6h-2zm-15 4a2.5 2.5 0 1 1-2.5 2.5A2.5 2.5 0 0 1 11 30zm14 0a2.5 2.5 0 1 1-2.5 2.5A2.5 2.5 0 0 1 25 30z"/></svg>`,
  car: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path fill="COLOR" stroke="#fff" stroke-width="2" d="M10 20l4-6h20l4 6v12h-4v-2H14v2h-4zm6-4l-2 4h20l-2-4zm-2 10a2 2 0 1 1-2 2 2 2 0 0 1 2-2zm20 0a2 2 0 1 1-2 2 2 2 0 0 1 2-2z"/></svg>`,
  suv: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path fill="COLOR" stroke="#fff" stroke-width="2" d="M8 18l3-4h26l3 4v14h-4v-3H12v3H8zm6-2l-2 2h24l-2-2zm-1 12a2.5 2.5 0 1 1-2.5 2.5A2.5 2.5 0 0 1 13 28zm22 0a2.5 2.5 0 1 1-2.5 2.5A2.5 2.5 0 0 1 35 28z"/></svg>`
};

const mapContainerStyle = {
  width: '100%',
  height: '700px'
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: true,
  fullscreenControl: true,
  gestureHandling: 'greedy'
};

function FleetTracking() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 51.947, lng: 1.054 });
  const [mapZoom, setMapZoom] = useState(13);
  const [countdown, setCountdown] = useState(30);
  const [refreshing, setRefreshing] = useState(false);
  const [fleetError, setFleetError] = useState('');
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [employees, setEmployees] = useState([]);
  const mapRef = useRef(null);

  // Restore saved map view on mount
  useEffect(() => {
    const savedView = localStorage.getItem('fleetMapView');
    if (savedView) {
      const { center, zoom } = JSON.parse(savedView);
      setMapCenter(center);
      setMapZoom(zoom);
    }
  }, []); // Run once on mount

  useEffect(() => {
    fetchVehicles();
    fetchEmployees();
    
    // Auto-refresh every 30 seconds if enabled
    let interval;
    let countdownInterval;
    
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchVehicles();
        setCountdown(30);
      }, 30000);
      
      // Countdown timer (updates every second)
      countdownInterval = setInterval(() => {
        setCountdown(prev => prev > 0 ? prev - 1 : 30);
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [autoRefresh]);

  const fetchVehicles = async () => {
    setRefreshing(true);
    try {
      const response = await apiFetch('/api/vehicles/locations');
      const data = await response.json();
      setVehicles(data);
      setFleetError('');
      setLastUpdate(new Date());
      setLoading(false);
      setCountdown(30); // Reset countdown on manual refresh
      
      // Auto-center map on first load if vehicles exist
      if (data.length > 0 && !mapRef.current) {
        const firstVehicle = data[0];
        setMapCenter({ lat: firstVehicle.coords.lat, lng: firstVehicle.coords.lon });
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setFleetError(error.message || 'Vehicle locations are unavailable.');
      setLoading(false);
    } finally {
      setRefreshing(false);
    }
  };
  
  const fetchEmployees = async () => {
    try {
      const response = await apiFetch('/api/employees');
      const data = await response.json();
      setEmployees(data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const onMapLoad = (map) => {
    mapRef.current = map;
    
    // Save view when user moves/zooms the map (with debounce)
    let saveTimeout;
    const saveHandler = () => {
      clearTimeout(saveTimeout);
      saveTimeout = setTimeout(() => {
        const center = map.getCenter();
        const zoom = map.getZoom();
        localStorage.setItem('fleetMapView', JSON.stringify({
          center: { lat: center.lat(), lng: center.lng() },
          zoom: zoom
        }));
      }, 500);
    };
    
    map.addListener('idle', saveHandler);
    map.addListener('zoom_changed', saveHandler);
  };
  
  const centerOnVehicle = (vehicle) => {
    setMapCenter({ lat: vehicle.coords.lat, lng: vehicle.coords.lon });
    setMapZoom(15);
    setSelectedVehicle(vehicle);
  };

  const getStatusColor = (lastUpdated) => {
    const now = new Date();
    const updated = new Date(lastUpdated);
    const minutesAgo = (now - updated) / 1000 / 60;
    
    if (minutesAgo < 5) return 'bg-green-100 text-green-800';
    if (minutesAgo < 60) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getStatusText = (lastUpdated) => {
    const now = new Date();
    const updated = new Date(lastUpdated);
    const minutesAgo = Math.floor((now - updated) / 1000 / 60);
    
    if (minutesAgo < 1) return 'Just now';
    if (minutesAgo < 60) return `${minutesAgo}m ago`;
    const hoursAgo = Math.floor(minutesAgo / 60);
    if (hoursAgo < 24) return `${hoursAgo}h ago`;
    const daysAgo = Math.floor(hoursAgo / 24);
    return `${daysAgo}d ago`;
  };

  const formatCoords = (lat, lon) => {
    return `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
  };

  const openInMaps = (lat, lon, name) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`;
    window.open(url, '_blank');
  };

  const saveVehicleAppearance = async (vehicle, iconType, markerColor, iconSize, employeeId) => {
    try {
      // Save appearance
      const appearanceResponse = await apiFetch(`/api/employee-vehicles/appearance/${vehicle.device_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          icon_type: iconType, 
          marker_color: markerColor,
          icon_size: iconSize,
          vehicle_name: vehicle.name
        })
      });

      if (!appearanceResponse.ok) {
        alert('Failed to update vehicle appearance');
        return;
      }

      // If employee changed, update assignment
      const currentEmployeeId = vehicle.assigned_employee?.id || null;
      if (employeeId !== currentEmployeeId) {
        if (employeeId) {
          // Assign to employee
          const assignResponse = await apiFetch('/api/employee-vehicles', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              employee_id: employeeId,
              device_id: vehicle.device_id,
              vehicle_name: vehicle.name,
              is_primary: true
            })
          });
          if (!assignResponse.ok) {
            alert('Failed to assign vehicle');
            return;
          }
        } else {
          // Unassign vehicle
          const unassignResponse = await apiFetch(`/api/employee-vehicles/${vehicle.device_id}`, {
            method: 'DELETE'
          });
          if (!unassignResponse.ok) {
            alert('Failed to unassign vehicle');
            return;
          }
        }
      }

      await fetchVehicles();
      setEditingVehicle(null);
    } catch (error) {
      console.error('Error updating vehicle appearance:', error);
      alert('Error updating vehicle appearance');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Header */}
      <PageHeader 
        title="Fleet Tracking"
        subtitle={`${vehicles.length} vehicle${vehicles.length !== 1 ? 's' : ''} tracked${lastUpdate ? ` • Last update: ${lastUpdate.toLocaleTimeString()}` : ''}`}
        backButton
        onBack={() => navigate('/ops/daily-planning')}
        backTitle="Back to Daily Planning"
        sticky
        actionButton={
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-white">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded"
              />
              <span>Auto {autoRefresh && `(${countdown}s)`}</span>
            </label>
            <button
              onClick={fetchVehicles}
              disabled={refreshing}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg font-medium transition border border-white/20 text-sm disabled:opacity-50"
            >
              {refreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
            </button>
          </div>
        }
      />

      {/* Content */}
      <div className="mx-auto py-6">
        {fleetError && <div role="alert" className="mx-6 mb-4 rounded border border-amber-300 bg-amber-50 p-4 text-amber-900">{fleetError} Live positions require a tracking account configured for your organization.</div>}
        
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-gray-600 mt-4">Loading vehicle locations...</p>
          </div>
        ) : (
          <>
            {/* Map View */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="bg-gray-100 border-b border-gray-200 px-6 py-3">
                <h2 className="font-semibold text-gray-900">📍 Live Map View</h2>
              </div>
              <div>
                {GOOGLE_MAPS_API_KEY ? (
                  <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
                    <GoogleMap
                      mapContainerStyle={mapContainerStyle}
                      center={mapCenter}
                      zoom={mapZoom}
                      options={mapOptions}
                      onLoad={onMapLoad}
                    >
                      {window.google && window.google.maps && vehicles.map((vehicle) => {
                        const iconType = vehicle.icon_type || 'van';
                        const iconSize = vehicle.icon_size || 36;
                        const iconSvg = VEHICLE_ICONS[iconType].replace('COLOR', vehicle.marker_color);
                        const halfSize = iconSize / 2;
                        
                        return (
                          <Marker
                            key={vehicle.device_id}
                            position={{ lat: vehicle.coords.lat, lng: vehicle.coords.lon }}
                            title={vehicle.name}
                            label={{
                              text: vehicle.name,
                              color: '#1f2937',
                              fontWeight: 'bold',
                              fontSize: '11px'
                            }}
                            icon={{
                              url: `data:image/svg+xml,${encodeURIComponent(iconSvg)}`,
                              scaledSize: new window.google.maps.Size(iconSize, iconSize),
                              anchor: new window.google.maps.Point(halfSize, halfSize),
                              labelOrigin: new window.google.maps.Point(halfSize, -6)
                            }}
                            onClick={() => setSelectedVehicle(vehicle)}
                          />
                        );
                      })}
                      
                      {selectedVehicle && (
                        <InfoWindow
                          position={{ lat: selectedVehicle.coords.lat, lng: selectedVehicle.coords.lon }}
                          onCloseClick={() => setSelectedVehicle(null)}
                        >
                          <div className="p-2">
                            <h3 className="font-bold text-gray-900 mb-1">{selectedVehicle.name}</h3>
                            <p className="text-sm text-gray-600">Speed: {selectedVehicle.speed} km/h</p>
                            <p className="text-sm text-gray-600">Battery: {selectedVehicle.battery}/6</p>
                            <p className="text-sm text-gray-600">Signal: {selectedVehicle.signal}%</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {getStatusText(selectedVehicle.last_updated)}
                            </p>
                            <button
                              onClick={() => openInMaps(selectedVehicle.coords.lat, selectedVehicle.coords.lon, selectedVehicle.name)}
                              className="mt-2 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                            >
                              Open in Maps
                            </button>
                          </div>
                        </InfoWindow>
                      )}
                    </GoogleMap>
                  </LoadScript>
                ) : (
                  <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg h-96 flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <p className="text-lg font-medium mb-2">🗺️ Google Maps API Key Required</p>
                      <p className="text-sm">Configure a Google Maps browser key for this service.</p>
                      <p className="text-xs mt-2">Map will appear once API key is configured</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Vehicle List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="bg-gray-100 border-b border-gray-200 px-6 py-3">
                <h2 className="font-semibold text-gray-900">🚗 Vehicle Status</h2>
              </div>
              
              {vehicles.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <p className="text-lg">No vehicles found</p>
                  <p className="text-sm mt-2">Check SafeTag API connection</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {vehicles.map((vehicle) => (
                    <div key={vehicle.device_id} className="p-6 hover:bg-gray-50 transition">
                      <div className="flex items-start justify-between">
                        
                        {/* Vehicle Info */}
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <div 
                              className="w-4 h-4 rounded-full border-2 border-white shadow"
                              style={{ backgroundColor: vehicle.marker_color || '#3b82f6' }}
                            ></div>
                            <h3 className="text-lg font-semibold text-gray-900">{vehicle.name}</h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(vehicle.last_updated)}`}>
                              {getStatusText(vehicle.last_updated)}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">Location</p>
                              <p className="font-mono text-gray-900">{formatCoords(vehicle.coords.lat, vehicle.coords.lon)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Speed</p>
                              <p className="text-gray-900">{vehicle.speed} km/h {vehicle.speed === 0 && '(Stationary)'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Battery</p>
                              <p className="text-gray-900">Level {vehicle.battery}/6 {vehicle.charging && '⚡'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Signal</p>
                              <p className="text-gray-900">{vehicle.signal}%</p>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="ml-6 flex flex-col space-y-2">
                          <button
                            onClick={() => setEditingVehicle(vehicle)}
                            className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition text-sm font-medium"
                          >
                            🎨 Customize
                          </button>
                          <button
                            onClick={() => centerOnVehicle(vehicle)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition text-sm font-medium"
                          >
                            📍 Show on Map
                          </button>
                          <button
                            onClick={() => openInMaps(vehicle.coords.lat, vehicle.coords.lon, vehicle.name)}
                            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition text-sm"
                          >
                            🗺️ Google Maps
                          </button>
                          {vehicle.assigned_employee && (
                            <button
                              onClick={() => navigate(`/employees/${vehicle.assigned_employee.id}`)}
                              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition text-sm"
                            >
                              👤 {vehicle.assigned_employee.name}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-4 gap-4 mt-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <p className="text-sm text-gray-500">Total Vehicles</p>
                <p className="text-2xl font-bold text-gray-900">{vehicles.length}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <p className="text-sm text-gray-500">{'Active (< 5 min)'}</p>
                <p className="text-2xl font-bold text-green-600">
                  {vehicles.filter(v => {
                    const minutesAgo = (new Date() - new Date(v.last_updated)) / 1000 / 60;
                    return minutesAgo < 5;
                  }).length}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <p className="text-sm text-gray-500">Moving</p>
                <p className="text-2xl font-bold text-blue-600">
                  {vehicles.filter(v => v.speed > 0).length}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <p className="text-sm text-gray-500">Charging</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {vehicles.filter(v => v.charging).length}
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Customize Vehicle Modal */}
      {editingVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Customize {editingVehicle.name}</h2>
            
            {/* Icon Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Icon</label>
              <div className="grid grid-cols-4 gap-3">
                {Object.keys(VEHICLE_ICONS).map(iconType => {
                  const iconSvg = VEHICLE_ICONS[iconType].replace('COLOR', editingVehicle.marker_color || '#3b82f6');
                  const isSelected = (editingVehicle.icon_type || 'van') === iconType;
                  
                  return (
                    <button
                      key={iconType}
                      onClick={() => setEditingVehicle({...editingVehicle, icon_type: iconType})}
                      className={`p-4 border-2 rounded-lg hover:border-blue-500 transition ${
                        isSelected ? 'border-blue-600 bg-blue-50' : 'border-gray-300'
                      }`}
                    >
                      <div dangerouslySetInnerHTML={{ __html: iconSvg }} />
                      <p className="text-xs mt-1 capitalize">{iconType}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color Picker */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Marker Color</label>
              <div className="grid grid-cols-6 gap-2">
                {['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#6366f1', '#f97316', '#14b8a6', '#a855f7'].map(color => (
                  <button
                    key={color}
                    onClick={() => setEditingVehicle({...editingVehicle, marker_color: color})}
                    className={`w-10 h-10 rounded-lg border-2 ${
                      (editingVehicle.marker_color || editingVehicle.marker_color) === color
                        ? 'border-gray-900 ring-2 ring-offset-2 ring-gray-900'
                        : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <div className="mt-3">
                <label className="text-xs text-gray-600">Custom Hex:</label>
                <input
                  type="text"
                  value={editingVehicle.marker_color || ''}
                  onChange={(e) => setEditingVehicle({...editingVehicle, marker_color: e.target.value})}
                  placeholder="#3b82f6"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>

            {/* Icon Size Slider */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Icon Size: {editingVehicle.icon_size || 36}px
              </label>
              <input
                type="range"
                min="24"
                max="64"
                step="4"
                value={editingVehicle.icon_size || 36}
                onChange={(e) => setEditingVehicle({...editingVehicle, icon_size: parseInt(e.target.value)})}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Small (24px)</span>
                <span>Medium (36px)</span>
                <span>Large (64px)</span>
              </div>
            </div>

            {/* Employee Assignment */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Assigned Employee</label>
              <select
                value={editingVehicle.assigned_employee?.id || ''}
                onChange={(e) => setEditingVehicle({
                  ...editingVehicle, 
                  assigned_employee: e.target.value ? { id: parseInt(e.target.value) } : null
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Unassigned</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Actions */}
            <div className="flex space-x-3">
              <button
                onClick={() => setEditingVehicle(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => saveVehicleAppearance(
                  editingVehicle,
                  editingVehicle.icon_type || 'van',
                  editingVehicle.marker_color,
                  editingVehicle.icon_size || 36,
                  editingVehicle.assigned_employee?.id || null
                )}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FleetTracking;
